import React, { useEffect, useState } from "react";
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, Chip, Avatar,
} from "@mui/material";
import { SideBarWidth } from "../../utils/SideBarWidth";
import { WalletsRequests } from "../../api/requests/Wallets/wallets";
import { Colors } from "../../Theme/Theme";
import PaymentsIcon from "@mui/icons-material/Payments";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";

export default function Transactions() {
  const [walletHistory, setWalletHistory] = useState([]);
  const [walletBalance, setWalletBalance] = useState("0");
  const [totalCredit, setTotalCredit] = useState("0");
  const [totalWithdrawal, setTotalWithdrawal] = useState("0");

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const response = await WalletsRequests.getWalletHistory();
        const data = response.data || [];
        setWalletHistory(data);

        let credit = 0, withdrawal = 0;
        data.forEach((t) => {
          if (t.type === "CREDIT") credit += parseFloat(t.amount);
          else if (t.type === "WITHDRAWAL") withdrawal += parseFloat(t.amount);
        });
        setTotalCredit(credit.toLocaleString("en-IN"));
        setTotalWithdrawal(withdrawal.toLocaleString("en-IN"));
        setWalletBalance((credit - withdrawal).toLocaleString("en-IN"));
      } catch (error) {}
    };
    fetchHistory();
  }, []);

  return (
    <Box sx={{
      width: { xs: "100%", md: `calc(100% - ${SideBarWidth}px)` },
      ml: { md: `${SideBarWidth}px` },
      minHeight: "100vh", backgroundColor: Colors.bgColor,
    }}>
      <Box sx={{ px: { xs: 2, md: 3 }, py: 3, maxWidth: 1200 }}>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <PaymentsIcon sx={{ fontSize: 22, color: Colors.primary }} />
            <Typography sx={{ fontWeight: 700, color: Colors.primaryTextColor, fontSize: { xs: "20px", md: "24px" }, letterSpacing: "-0.02em" }}>
              Transactions
            </Typography>
          </Box>
          <Typography sx={{ color: Colors.secondaryColor, fontSize: "13px" }}>
            View your complete wallet transaction history.
          </Typography>
        </Box>

        {/* Wallet Balance + Stats */}
        <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
          <Box sx={{
            flex: "1 1 180px", p: 2.5, borderRadius: "12px",
            backgroundColor: Colors.white, border: `1px solid ${Colors.dividerColor}`,
          }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <AccountBalanceWalletIcon sx={{ fontSize: 18, color: "#059669" }} />
              <Typography sx={{ fontSize: "11px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.6px", color: Colors.secondaryColor }}>
                Wallet Balance
              </Typography>
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: "24px", lineHeight: 1.2, color: "#059669" }}>
              Rs. {walletBalance}
            </Typography>
          </Box>
          <Box sx={{ flex: "1 1 140px", p: 2, backgroundColor: Colors.white, borderRadius: "10px", border: `1px solid ${Colors.dividerColor}` }}>
            <Typography sx={{ fontSize: "11px", fontWeight: 600, color: Colors.secondaryColor, textTransform: "uppercase", letterSpacing: "0.5px", mb: 0.5 }}>
              Total Credit
            </Typography>
            <Typography sx={{ fontWeight: 700, fontSize: "20px", color: Colors.insuranceGreen }}>
              Rs. {totalCredit}
            </Typography>
          </Box>
          <Box sx={{ flex: "1 1 140px", p: 2, backgroundColor: Colors.white, borderRadius: "10px", border: `1px solid ${Colors.dividerColor}` }}>
            <Typography sx={{ fontSize: "11px", fontWeight: 600, color: Colors.secondaryColor, textTransform: "uppercase", letterSpacing: "0.5px", mb: 0.5 }}>
              Total Withdrawn
            </Typography>
            <Typography sx={{ fontWeight: 700, fontSize: "20px", color: "#EF4444" }}>
              Rs. {totalWithdrawal}
            </Typography>
          </Box>
          <Box sx={{ flex: "1 1 100px", p: 2, backgroundColor: Colors.white, borderRadius: "10px", border: `1px solid ${Colors.dividerColor}` }}>
            <Typography sx={{ fontSize: "11px", fontWeight: 600, color: Colors.secondaryColor, textTransform: "uppercase", letterSpacing: "0.5px", mb: 0.5 }}>
              Transactions
            </Typography>
            <Typography sx={{ fontWeight: 700, fontSize: "20px", color: Colors.primaryTextColor }}>
              {walletHistory.length}
            </Typography>
          </Box>
        </Box>

        {/* Table */}
        <Box sx={{ backgroundColor: Colors.white, borderRadius: "12px", border: `1px solid ${Colors.dividerColor}`, overflow: "hidden" }}>
          <Box sx={{ px: 3, py: 2 }}>
            <Typography sx={{ fontWeight: 700, fontSize: "15px", color: Colors.primaryTextColor }}>
              Wallet History
            </Typography>
          </Box>
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table>
              <TableHead>
                <TableRow>
                  {["Type", "Amount (Rs.)", "Date"].map((header) => (
                    <TableCell key={header} sx={{
                      color: Colors.secondaryColor, py: 1.5, px: 3,
                      fontWeight: 600, fontSize: "11px", letterSpacing: "0.5px",
                      textTransform: "uppercase", backgroundColor: Colors.bgColor,
                      borderBottom: `1px solid ${Colors.dividerColor}`,
                    }}>
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {walletHistory.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} sx={{ py: 6, textAlign: "center", borderBottom: "none" }}>
                      <Typography sx={{ color: Colors.light_gray, fontSize: "14px" }}>No transactions found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  walletHistory.map((transaction) => {
                    const isCredit = transaction.type === "CREDIT";
                    return (
                      <TableRow key={transaction.id} sx={{ "&:last-child td": { borderBottom: "none" }, "&:hover": { backgroundColor: Colors.bgColor } }}>
                        <TableCell sx={{ borderBottom: `1px solid ${Colors.dividerColor}`, py: 1.5, px: 3 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            <Avatar sx={{
                              width: 32, height: 32,
                              backgroundColor: isCredit ? Colors.greenSoft : "#FEF2F2",
                              color: isCredit ? Colors.insuranceGreen : "#EF4444",
                            }}>
                              {isCredit ? <ArrowUpwardIcon sx={{ fontSize: 16 }} /> : <ArrowDownwardIcon sx={{ fontSize: 16 }} />}
                            </Avatar>
                            <Chip
                              label={transaction.type}
                              size="small"
                              sx={{
                                height: 22, fontSize: "11px", fontWeight: 600,
                                backgroundColor: isCredit ? Colors.greenSoft : "#FEF2F2",
                                color: isCredit ? Colors.insuranceGreen : "#EF4444",
                                "& .MuiChip-label": { px: 1 },
                              }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell sx={{ borderBottom: `1px solid ${Colors.dividerColor}`, py: 1.5, px: 3 }}>
                          <Typography sx={{
                            fontWeight: 600, fontSize: "14px",
                            color: isCredit ? Colors.insuranceGreen : "#EF4444",
                          }}>
                            {isCredit ? "+" : "-"} Rs. {transaction.amount}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ borderBottom: `1px solid ${Colors.dividerColor}`, py: 1.5, px: 3 }}>
                          <Typography sx={{ fontSize: "13px", color: Colors.secondaryColor }}>
                            {new Date(transaction.timestamp).toLocaleString()}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    </Box>
  );
}
